import React, { useState, useEffect } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { useClickOutside } from "../../Hooks/useClickOutside";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { uploadToCloudinary } from "../../../utils/cloudinary"; 
import { Loader2 } from "lucide-react";

const AddBannerModal = ({ onClose, isAddModal, fetchBanners, editData }) => {
  const [imageFile, setImageFile] = useState(null);
  const [bannerData, setBannerData] = useState({
    title: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
    subTitle: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
  });
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const domNode = useClickOutside(() => handleClose());

  // Helper to get multilingual data from API response
  const getMultilingualValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return {
        en: value.en || value.english || "",
        hi: value.hi || value.hindi || "",
        es: value.es || value.spanish || "",
        zh: value.zh || value.chinese || "",
      };
    }
    return "";
  };

  // Initialize form data when editing
  useEffect(() => {
    if (editData) {
      setBannerData({
        title: getMultilingualValue(editData.title),
        subTitle: getMultilingualValue(editData.subTitle),
      });
    }
  }, [editData]);

  useEffect(() => {
    if (isAddModal) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isAddModal]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleChange = (e, field, lang = null) => {
    const { value } = e.target;
    
    if (field === "title" || field === "subTitle") {
      setBannerData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!bannerData.title.en?.trim()) {
      toast.error("English title is required");
      return;
    }

    if (!editData && !imageFile) {
      toast.error("Please upload an image");
      return;
    }

    try {
      setLoading(true);

      let imageUrl = editData?.imgUrl || "";

      if (imageFile) {
        const uploaded = await uploadToCloudinary(imageFile);
        imageUrl = uploaded.secure_url;
      }

      const payload = {
        title: {
          en: bannerData.title.en?.trim() || "",
          hi: bannerData.title.hi?.trim() || bannerData.title.en?.trim() || "",
          es: bannerData.title.es?.trim() || bannerData.title.en?.trim() || "",
          zh: bannerData.title.zh?.trim() || bannerData.title.en?.trim() || "",
        },
        subTitle: {
          en: bannerData.subTitle.en?.trim() || "",
          hi: bannerData.subTitle.hi?.trim() || bannerData.subTitle.en?.trim() || "",
          es: bannerData.subTitle.es?.trim() || bannerData.subTitle.en?.trim() || "",
          zh: bannerData.subTitle.zh?.trim() || bannerData.subTitle.en?.trim() || "",
        },
        imgUrl: imageUrl,
      };

      if (editData) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/banners/update-banner/${editData._id}`,
          payload
        );
        Swal.fire({
          icon: "success",
          title: "Banner Updated",
          text: "The banner has been updated successfully.",
          confirmButtonColor: "#3085d6",
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/banners/create-banner`,
          payload
        );
        Swal.fire({
          icon: "success",
          title: "Banner Added",
          text: "Your new banner has been added successfully.",
          confirmButtonColor: "#3085d6",
        });
      }

      fetchBanners();
      handleClose();
    } catch (error) {
      console.error("Error saving banner:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.response?.data?.message || "Something went wrong while saving the banner.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className={`h-screen w-full fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center transition-opacity duration-200 z-50 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        ref={domNode}
        className={`bg-[#ecf1f5] w-[95%] sm:w-[90%] md:w-[85%] lg:w-[75%] xl:w-[70%] p-6 rounded-xl relative transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          animate ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-5"
        }`}
      >
        <button onClick={handleClose} className="absolute top-3 right-3 text-2xl p-2 hover:text-red-500 transition-colors">
          {icons.closeIcon}
        </button>

        <h1 className="text-2xl mb-4 font-philosopher text-primaryTextColor">
          {editData ? "Edit Banner" : "Add New Banner"} (Multilingual)
        </h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Title Section - All Languages */}
          <div className="space-y-4">
            <label className="block text-lg font-medium text-gray-700">
              Banner Title <span className="text-[#ff5200]">*</span>
              <span className="text-sm text-gray-500 ml-2">(Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* English Title */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English *</span>
                  {!bannerData.title.en?.trim() && (
                    <span className="text-xs text-red-500">Required</span>
                  )}
                </div>
                <input
                  type="text"
                  value={bannerData.title.en || ""}
                  onChange={(e) => handleChange(e, "title", "en")}
                  required
                  className="w-full border border-blue-300 bg-blue-50 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500"
                  placeholder="English title"
                />
              </div>

              {/* Hindi Title */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <input
                  type="text"
                  value={bannerData.title.hi || ""}
                  onChange={(e) => handleChange(e, "title", "hi")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500"
                  placeholder="Hindi title"
                />
              </div>

              {/* Spanish Title */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <input
                  type="text"
                  value={bannerData.title.es || ""}
                  onChange={(e) => handleChange(e, "title", "es")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500"
                  placeholder="Spanish title"
                />
              </div>

              {/* Chinese Title */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <input
                  type="text"
                  value={bannerData.title.zh || ""}
                  onChange={(e) => handleChange(e, "title", "zh")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500"
                  placeholder="Chinese title"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English title is required. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Subtitle Section - All Languages */}
          <div className="space-y-4">
            <label className="block text-lg font-medium text-gray-700">
              Banner Subtitle
              <span className="text-sm text-gray-500 ml-2">(Optional - Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* English Subtitle */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English</span>
                </div>
                <textarea
                  value={bannerData.subTitle.en || ""}
                  onChange={(e) => handleChange(e, "subTitle", "en")}
                  className="w-full border border-blue-300 bg-blue-50 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500 h-28"
                  placeholder="English subtitle"
                />
              </div>

              {/* Hindi Subtitle */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <textarea
                  value={bannerData.subTitle.hi || ""}
                  onChange={(e) => handleChange(e, "subTitle", "hi")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500 h-28"
                  placeholder="Hindi subtitle"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Spanish Subtitle */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <textarea
                  value={bannerData.subTitle.es || ""}
                  onChange={(e) => handleChange(e, "subTitle", "es")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500 h-28"
                  placeholder="Spanish subtitle"
                />
              </div>

              {/* Chinese Subtitle */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <textarea
                  value={bannerData.subTitle.zh || ""}
                  onChange={(e) => handleChange(e, "subTitle", "zh")}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 outline-none focus:ring-2 ring-blue-500 h-28"
                  placeholder="Chinese subtitle"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English subtitle is recommended. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Image Upload Section */}
          <div className="space-y-4">
            <label className="block text-lg font-medium text-gray-700">
              Upload Banner Image {!editData && <span className="text-[#ff5200]">*</span>}
            </label>
            <label
              htmlFor="uploadBanner"
              className="border-2 border-dashed border-blue-400 py-12 px-4 rounded-xl flex flex-col items-center justify-center cursor-pointer relative overflow-hidden bg-blue-50/50 hover:bg-blue-50 transition-colors"
            >
              {imageFile ? (
                <div className="relative w-full h-64">
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm bg-black/60 px-3 py-1 rounded">Change Image</span>
                  </div>
                </div>
              ) : editData?.imgUrl ? (
                <div className="relative w-full h-64">
                  <img
                    src={editData.imgUrl}
                    alt="existing-banner"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-sm bg-black/60 px-3 py-1 rounded">Change Image</span>
                  </div>
                </div>
              ) : (
                <>
                  <span className="text-blue-600 text-4xl mb-3">{icons.uploadIcon}</span>
                  <p className="text-gray-700 font-medium">Click to upload image</p>
                  <p className="text-sm text-gray-500 mt-1">or drag and drop</p>
                  <p className="text-xs text-gray-400 mt-2">Recommended: 1920x500px or similar banner size</p>
                </>
              )}
            </label>
            <input
              type="file"
              id="uploadBanner"
              className="hidden"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
            <p className="text-xs text-gray-500">
              {editData ? "Leave empty to keep current image" : "Image is required for new banners"}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2.5 mr-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-300 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center justify-center px-8 py-2.5 rounded-lg font-medium transition-colors duration-300 min-w-[140px] ${
                loading
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-[#b07d17] hover:bg-[#8c6312] text-white"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {editData ? "Updating..." : "Saving..."}
                </>
              ) : editData ? "Update Banner" : "Save Banner"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AddBannerModal;




// import React, { useState, useEffect } from "react";
// import { icons } from "../../../Assets/Icons/icons";
// import { useClickOutside } from "../../Hooks/useClickOutside";
// import axios from "axios";
// import Swal from "sweetalert2";
// import toast from "react-hot-toast";
// import { uploadToCloudinary } from "../../../utils/cloudinary"; 

// const AddBannerModal = ({ onClose, isAddModal, fetchBanners, editData }) => {
//   const [imageFile, setImageFile] = useState(null);
//   const [bannerData, setBannerData] = useState({
//     title: editData?.title || "",
//     subTitle: editData?.subTitle || "",
//   });
//   const [loading, setLoading] = useState(false);
//   const [animate, setAnimate] = useState(false);
//   const domNode = useClickOutside(() => handleClose());

//   useEffect(() => {
//     if (isAddModal) {
//       setTimeout(() => setAnimate(true), 10);
//     }
//   }, [isAddModal]);

//   const handleClose = () => {
//     setAnimate(false);
//     setTimeout(onClose, 250);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setBannerData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!bannerData.title) {
//       toast.error("Banner title is required");
//       return;
//     }

//     if (!editData && !imageFile) {
//       toast.error("Please upload an image");
//       return;
//     }

//     try {
//       setLoading(true);

//       let imageUrl = editData?.imgUrl || "";

//       if (imageFile) {
//         const uploaded = await uploadToCloudinary(imageFile);
//         imageUrl = uploaded.secure_url;
//       }

//       const payload = {
//         title: bannerData.title,
//         subTitle: bannerData.subTitle,
//         imgUrl: imageUrl,
//       };

//       if (editData) {
//         await axios.put(
//           `${process.env.REACT_APP_API_URL}/banners/update-banner/${editData._id}`,
//           payload
//         );
//         Swal.fire({
//           icon: "success",
//           title: "Banner Updated",
//           text: "The banner has been updated successfully.",
//           confirmButtonColor: "#3085d6",
//         });
//       } else {
//         await axios.post(
//           `${process.env.REACT_APP_API_URL}/banners/create-banner`,
//           payload
//         );
//         Swal.fire({
//           icon: "success",
//           title: "Banner Added",
//           text: "Your new banner has been added successfully.",
//           confirmButtonColor: "#3085d6",
//         });
//       }

//       fetchBanners();
//       handleClose();
//     } catch (error) {
//       console.error("Error saving banner:", error);
//       Swal.fire({
//         icon: "error",
//         title: "Oops...",
//         text: "Something went wrong while saving the banner.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <section
//       className={`h-screen w-full fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center transition-opacity duration-200 z-50 ${
//         animate ? "opacity-100" : "opacity-0"
//       }`}
//     >
//       <div
//         ref={domNode}
//         className={`bg-[#ecf1f5] w-[95%] sm:w-[90%] md:w-[70%] lg:w-[50%] xl:w-[40%] p-6 rounded-xl relative transition-all duration-300 ${
//           animate ? "scale-100" : "scale-95"
//         }`}
//       >
//         <button onClick={handleClose} className="absolute top-3 right-3 text-2xl">
//           {icons.closeIcon}
//         </button>

//         <h1 className="text-2xl mb-4 font-philosopher text-primaryTextColor">
//           {editData ? "Edit Banner" : "Add New Banner"}
//         </h1>

//         <form onSubmit={handleSubmit} className="flex flex-col gap-5">
//           <div>
//             <label className="block font-medium">Banner Title *</label>
//             <input
//               type="text"
//               name="title"
//               value={bannerData.title}
//               onChange={handleChange}
//               required
//               className="w-full border rounded-lg px-3 py-2 mt-1 outline-none focus:ring-2 ring-blue-500"
//               placeholder="Enter banner title"
//             />
//           </div>

//           <div>
//             <label className="block font-medium">Banner Subtitle</label>
//             <input
//               type="text"
//               name="subTitle"
//               value={bannerData.subTitle}
//               onChange={handleChange}
//               className="w-full border rounded-lg px-3 py-2 mt-1 outline-none focus:ring-2 ring-blue-500"
//               placeholder="Enter banner subtitle (optional)"
//             />
//           </div>

//           <div>
//             <label className="block font-medium">Upload Banner Image *</label>
//             <label
//               htmlFor="uploadBanner"
//               className="border-2 border-dashed border-blue-400 py-10 px-4 rounded-lg flex flex-col items-center justify-center cursor-pointer relative overflow-hidden"
//             >
//               {imageFile ? (
//                 <img
//                   src={URL.createObjectURL(imageFile)}
//                   alt="preview"
//                   className="w-full h-40 object-cover rounded-lg"
//                 />
//               ) : editData?.imgUrl ? (
//                 <img
//                   src={editData.imgUrl}
//                   alt="existing-banner"
//                   className="w-full h-40 object-cover rounded-lg"
//                 />
//               ) : (
//                 <>
//                   <span className="text-blue-600 text-3xl mb-2">{icons.uploadIcon}</span>
//                   <p>Click to upload image</p>
//                 </>
//               )}
//             </label>
//             <input
//               type="file"
//               id="uploadBanner"
//               className="hidden"
//               accept="image/*"
//               onChange={(e) => setImageFile(e.target.files[0])}
//             />
//           </div>

//           <div className="flex justify-end">
//             <button
//               type="submit"
//               disabled={loading}
//               className={`${
//                 loading ? "opacity-70 cursor-not-allowed" : ""
//               } bg-[#b07d17] hover:bg-[#8c6312] text-white font-semibold px-6 py-2 rounded-lg`}
//             >
//               {loading ? "Saving..." : editData ? "Update Banner" : "Save Banner"}
//             </button>
//           </div>
//         </form>
//       </div>
//     </section>
//   );
// };

// export default AddBannerModal;
